import * as R from 'ramda';

import { LUMIQ_ORGANIZATION_NAME } from '@/constants';
import { growthbook } from '@/services';
import {
  ContentOverride,
  LearningPath,
  LearningPathWithDuration,
  SessionSummary,
} from '@/types';
import { camelCaseKeys } from '@/utils';

import { swapCDNImageHost } from '../store.utils';

const sumDurationAndCredits = (learningPath: any) => {
  const { duration, credits } = learningPath.episodes.reduce(
    (acc: { duration: number; credits: number }, episode: any) => {
      acc.duration += episode.duration || 0;
      acc.credits += episode.credits || 0;
      return acc;
    },
    { duration: 0, credits: 0 },
  );

  return {
    ...learningPath,
    duration,
    credits,
  };
};

const castCreditsFromStringToNumber = (lp: any) => ({
  ...lp,
  episodes: lp.episodes.map((e: any) => ({ ...e, credits: Number(e.credits) })),
});

const calculateProgress = ({
  lps,
  playerSessions,
}: {
  lps: LearningPath[];
  playerSessions: Record<string, SessionSummary>;
}): LearningPathWithDuration[] => {
  return lps.map(lp => {
    const { durationPlayed, completions, lastPositionEventAt } =
      lp.episodes.reduce(
        (acc, episode) => {
          const session = playerSessions[episode.episodeId];
          if (!session) return acc;

          const isCompleted =
            session.status === 'verified' || session.status === 'completed';

          const candidateTime = session.lastPositionEventAt
            ? new Date(session.lastPositionEventAt).getTime()
            : -Infinity;
          const isMoreRecent = candidateTime > acc.lastPositionEventAtTime;

          return {
            durationPlayed:
              acc.durationPlayed +
              ((isCompleted
                ? session.episodeLengthSeconds
                : session.playedDuration) || 0),
            completions: acc.completions + (isCompleted ? 1 : 0),
            lastPositionEventAt: isMoreRecent
              ? session.lastPositionEventAt
              : acc.lastPositionEventAt,
            lastPositionEventAtTime: isMoreRecent
              ? candidateTime
              : acc.lastPositionEventAtTime,
          };
        },
        {
          durationPlayed: 0,
          completions: 0,
          lastPositionEventAt: '',
          lastPositionEventAtTime: -Infinity,
        },
      );

    return {
      ...lp,
      durationPlayed,
      completions,
      isCompleted: completions === lp.episodes.length,
      lastPositionEventAt,
    };
  });
};

const filterForFeaturedLearningPaths = ({ lps }: { lps: LearningPath[] }) =>
  lps
    .filter(lp => lp.organizationName === LUMIQ_ORGANIZATION_NAME)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )
    .slice(0, 10);

export const transformLearningPath: (arg: unknown) => LearningPath = arg =>
  R.pipe(
    camelCaseKeys,
    updateAssetCDN,
    applyLearningPathOverrides,
    castCreditsFromStringToNumber,
    sumDurationAndCredits,
  )(arg);

const updateAssetCDN = (lp: LearningPath): LearningPath => {
  return {
    ...lp,
    coverImage: swapCDNImageHost(lp.coverImage),
    headerImage: swapCDNImageHost(lp.headerImage),
  };
};

export const applyLearningPathOverrides = (lp: LearningPath): LearningPath => {
  const overrides = growthbook
    .getFeatureValue('content-overrides', {
      data: [] as ContentOverride[],
    })
    .data.find(
      override =>
        override.content_type === 'learning_path' &&
        Number(override.content_id) === lp.groupId,
    );
  return { ...lp, ...overrides };
};

export const LearningPathsUtils = {
  calculateProgress,
  filterForFeaturedLearningPaths,
};
